import {
  Button as HeadlessButton,
  type ButtonProps as HeadlessButtonProps,
} from "@headlessui/react";
import { Loading } from "@winamp/icons/Loading.js";
import type { VariantProps } from "class-variance-authority";
import React, { type ComponentPropsWithoutRef, type ForwardedRef } from "react";
import type { ClassNameValue } from "tailwind-merge";
import { classNames } from "../../utils/classNames";
import { Link } from "../Link";
import { TouchTarget } from "../TouchTarget";
import { buttonVariants } from "./variants";

export type ButtonProps = VariantProps<typeof buttonVariants> & {
  children?: React.ReactNode;
  loading?: boolean;
} & (
    | (ComponentPropsWithoutRef<typeof Link> & { disabled?: boolean })
    | HeadlessButtonProps
  );

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  function Button(
    {
      children,
      className,
      color = "dark/zinc",
      icon = false,
      size = "md",
      variant = "primary",
      loading,
      disabled,
      ...props
    },
    ref,
  ) {
    const classNameList = (
      bag: {
        disabled: boolean;
        hover: boolean;
        focus: boolean;
        active: boolean;
        autofocus: boolean;
      } = {
        disabled: false,
        hover: false,
        focus: false,
        active: false,
        autofocus: false,
      },
      additionalClasses: ClassNameValue = "",
    ) => {
      const classList =
        typeof className === "function" ? className(bag) : className;

      return classNames(
        buttonVariants({
          color,
          icon,
          size,
          variant,
        }),
        classList,
        {
          "[&>[data-slot=icon]]:hidden": loading,
        },
        additionalClasses,
      );
    };

    const isDisabled = disabled || loading;

    return "to" in props ? (
      <Link
        role="button"
        className={classNameList()}
        {...props}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        {...(isDisabled ? { "data-disabled": true } : {})}
      >
        <TouchTarget>
          {loading && <Loading data-slot="loader" className="animate-spin" />}
          {children}
        </TouchTarget>
      </Link>
    ) : (
      <HeadlessButton
        role="button"
        className={(bag) => classNameList(bag)}
        {...props}
        ref={ref}
        disabled={isDisabled}
      >
        <TouchTarget>
          {loading && <Loading data-slot="loader" className="animate-spin" />}
          {children}
        </TouchTarget>
      </HeadlessButton>
    );
  },
);
